/* eslint-disable require-await */
import React, { useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Field } from 'formik';
import debounce from 'lodash/debounce';
import { VENDORS_QUERY } from 'utils/CommonGraphQL';
import { defaultDeleteFilter } from 'utils/constants';
import FormikAsyncCreatableSelect from 'components/Fields/FormikAsyncCreatableSelect';

const defaultGetTraderLabel = (option, keysName) => {
  if (!option) return '';
  if (typeof option === 'string') return option;
  if (keysName && option[keysName]) return option[keysName];
  const fullName = [option?.firstName, option?.middleName, option?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  const entity = option?.entityName || option?.tradeName || '';
  if (entity && fullName && entity.toLowerCase() !== fullName.toLowerCase()) {
    return `${fullName} (${entity})`;
  }
  return fullName || entity || option?.phone || String(option?.id || '');
};

const TraderSelectField = ({
  filter = [],
  statusFilter,
  keysName,
  searchKey = 'traderSearchFilter',
  op = 'LIKE_OR_NULL',
  allowNewOption = true,
  getOptionLabel,
  ...props
}) => {
  const [traders] = useLazyQuery(VENDORS_QUERY);
  const [isLoading, setisLoading] = useState(false);

  const formatOptionLabel = (option) => {
    if (getOptionLabel) return getOptionLabel(option);
    return defaultGetTraderLabel(option, keysName);
  };

  const loadOptions = async (inputValue, callback) => {
    if (!inputValue) {
      return [];
    }

    try {
      setisLoading(true);
      const { data } = await traders({
        variables: {
          filter: [
            {
              key: searchKey || keysName || 'traderSearchFilter',
              value: inputValue,
              type: 'STRING',
              op: op || 'LIKE_OR_NULL',
            },
            { ...defaultDeleteFilter },
            ...(statusFilter ? [statusFilter] : []),
            ...filter,
          ],
          pagination: false,
        },
      });

      const traderOptions = (data?.traders?.traders || []).map((trader) => ({
        ...trader,
        label: formatOptionLabel(trader),
      }));

      callback(traderOptions);
      setisLoading(false);
    } catch (error) {
      console.error('Error fetching options:', error);
      callback([]);
      setisLoading(false);
    }
  };

  const handleDebounce = debounce(async (inputValue, callback) => {
    loadOptions(inputValue, callback);
  }, 1000);

  return (
    <div>
      {allowNewOption ? (
        <Field
          type="text"
          getOptionValue={(option) => (option?.id !== undefined ? option.id : option)}
          getOptionLabel={formatOptionLabel}
          formatOptionLabel={formatOptionLabel}
          keys={{ id: 'id', label: 'label' }}
          loadOptions={handleDebounce}
          component={FormikAsyncCreatableSelect}
          isLoading={isLoading}
          isClearable
          isSearchable
          getNewOptionData={(newValue) => {
            if (newValue) {
              return {
                id: newValue,
                firstName: newValue,
                entityName: newValue,
                ...(keysName ? { [keysName]: newValue } : {}),
              };
            }
          }}
          {...props}
        />
      ) : (
        <Field
          type="text"
          getOptionValue={(option) => (option?.id !== undefined ? option.id : option)}
          getOptionLabel={formatOptionLabel}
          formatOptionLabel={formatOptionLabel}
          keys={{ id: 'id', label: 'label' }}
          loadOptions={handleDebounce}
          component={FormikAsyncCreatableSelect}
          isLoading={isLoading}
          isClearable
          isSearchable
          {...props}
        />
      )}
    </div>
  );
};

export default TraderSelectField;
